import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, ViewChild } from "@angular/core";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { CopyDeckRequest, CreateDeckResponse, Deck, VisibilityType } from "@apiserver-rest/ponder-data-client-base";
import { SvgIcon, SvgIconMap } from "@ponder-shared/utils/svgIcons";
import { VisibilityTypes } from "@ponder-shared/utils/visbilityTypes";
import { take } from "rxjs";
import { getDeckRoute } from "../../deck-router";
import { AuthService } from "@ponder-core/auth/auth.service";
import { keepInBoundsX } from "@ponder-shared/utils/keepInBounds";
import { AuthSession } from "@ponder-core/auth/auth-session";

@Component({
    selector: "deck-copy",
    imports: [],
    templateUrl: "./deck-copy.component.html"
})
export class DeckCopyComponent {
    @ViewChild("dialog", { static: false }) public dialogRef?: ElementRef<HTMLDivElement>;
    @Input({ required: true }) public deck!: Deck;

    public readonly copySvgIcon: string = SvgIconMap.get(SvgIcon.Copy)!;
    public readonly closeSvgIcon: string = SvgIconMap.get(SvgIcon.Close)!;
    public readonly undoSvgIcon: string = SvgIconMap.get(SvgIcon.Undo)!;
    public readonly busySpinSvg = SvgIconMap.get(SvgIcon.BusySpin)!;
    public readonly visibilityTypes = VisibilityTypes;

    @ViewChild("nameInput") private nameInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild("visibilityInput") private visiiblityInputRef!: ElementRef<HTMLSelectElement>;
    @ViewChild("groupInput") private groupInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild("colorInput") private colorInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild("actionInput") private actionInputRef!: ElementRef<HTMLSelectElement>;

    public state = CopyState.Hidden;
    public deckUrl?: string;
    private username?: string;
    public errorMessage?: string;

    public constructor(
        private elementRef: ElementRef, 
        private ponderDataClient: PonderDataClient, 
        private auth: AuthService,
        private changeDetector: ChangeDetectorRef) {

    }

    public ngOnInit(): void {
        this.deckPreviewColor = this.deck.color!;
        
        this.auth.getSessionAsync().then((authSession?: AuthSession) => {
            this.username = authSession?.username;
        })
    }

    public onCopyDeckClicked(): void {
        if (this.isVisible()) {
            this.onClose();
        } else {
            this.onOpen();
        }
    }

    // -------------------------------------------------------------------------------------
    // Global Events
    // -------------------------------------------------------------------------------------

    private mouseDownOutside: boolean = false;

    @HostListener("document:keydown.escape", ["$event"]) 
    private onEscapePressed(event: KeyboardEvent): void {
        this.onClose();
    }

    @HostListener("document:mousedown", ["$event"])
    private onGlobalMouseDown(event: MouseEvent): void {
        if ((this.state != CopyState.Hidden) && !this.elementRef.nativeElement.contains(event.target as HTMLInputElement)) {
            this.mouseDownOutside = true;
        }
    }

    @HostListener("document:mouseup", ["$event"])
    private onGlobalMouseUp(event: MouseEvent): void {
        // Did the user click outside of our modal panel?
        if ((this.state != CopyState.Hidden) && this.mouseDownOutside && !this.elementRef.nativeElement.contains((event.target as HTMLInputElement))) {
            this.onClose();
        }

        this.mouseDownOutside = false;
    }

    // -------------------------------------------------------------------------------------
    // State
    // -------------------------------------------------------------------------------------

    private onOpen(): void {
        this.state = CopyState.Visible;

        if (!this.dialogRef) {
            this.changeDetector.detectChanges();
        }
        
        if (this.dialogRef) {
            keepInBoundsX(this.dialogRef.nativeElement, 10);
        }
    }

    private onClose(): void {
        this.state = CopyState.Hidden;
        this.deckUrl = undefined;
    }

    public isVisible(): boolean {
        return (this.state != CopyState.Hidden);
    }

    public isReady(): boolean {
        return (this.state == CopyState.Visible);
    }

    public isCopying(): boolean {
        return this.state == CopyState.Copying;
    }

    public isDoneSuccess(): boolean {
        return this.state == CopyState.DoneSuccess;
    }

    public isDoneFailure(): boolean {
        return this.state == CopyState.DoneFailure;
    }

    // -------------------------------------------------------------------------------------
    // On Color Change
    // -------------------------------------------------------------------------------------
    
    public deckPreviewColor!: string;
    public prevColor: string | undefined;

    public onColorChange(value: string): void {
        this.prevColor = this.deckPreviewColor;
        this.deckPreviewColor = value;
    }

    public onUndoColorClicked(event: Event): void {
        event.stopPropagation();
        event.preventDefault();

        this.onColorChange(this.prevColor!);
        this.prevColor = undefined;
    }
    
    // -------------------------------------------------------------------------------------
    // On Cancel
    // -------------------------------------------------------------------------------------

    public onCancelClicked(): void {
        this.onClose();
    }

    // -------------------------------------------------------------------------------------
    // On Copy Confirm
    // -------------------------------------------------------------------------------------

    public onConfirmCopyClicked(): void {
        if (this.state != CopyState.Visible || !this.username) {
            return;
        }

        this.state = CopyState.Copying;
        
        var action = this.actionInputRef.nativeElement.value as CardCopyActionType;

        var copyRequest = new CopyDeckRequest({
            deckId: this.deck.id,
            name: this.nameInputRef.nativeElement.value,
            visibility: this.visiiblityInputRef.nativeElement.value as VisibilityType,
            group: this.groupInputRef.nativeElement.value,
            color: this.colorInputRef.nativeElement.value,
            addToCollection: (action != CardCopyActionType.None),
            skipIfCollected: (action != CardCopyActionType.All)
        });

        this.ponderDataClient
            .deckCopy(copyRequest)
            .pipe(take(1))
            .subscribe({
                next: (response: CreateDeckResponse) => {
                    if (response?.deckId) {
                        // todo - even on success there can be an error message. typically because not all cards could be added to their collection due to limits.
                        this.state = CopyState.DoneSuccess;
                        this.deckUrl = getDeckRoute(this.username!, response.deckId);
                    } else {
                        this.errorMessage = response.error;
                        this.state = CopyState.DoneFailure;
                    }
                },
                error: () => { this.state = CopyState.DoneFailure; }
            });
    }
}

enum CardCopyActionType 
{
    None = "none",
    All = "all",
    New = "new"
};

enum CopyState
{
    Hidden,
    Visible,
    Copying,
    DoneSuccess,
    DoneFailure
}